/**
 * Admin AnnouncementsPage
 *
 * Create and manage the site-wide banner shown to all visitors.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import {
  useAnnouncements,
  AnnouncementType,
  NewAnnouncement,
} from './hooks/useAnnouncements';

const TYPE_STYLES: Record<AnnouncementType, string> = {
  info: 'bg-blue-100 text-blue-800',
  promo: 'bg-primary-100 text-primary-800',
  warning: 'bg-amber-100 text-amber-800',
};

const EMPTY: NewAnnouncement = {
  message: '',
  type: 'info',
  active: true,
  ctaLabel: '',
  ctaUrl: '',
};

export const AnnouncementsPage: React.FC = () => {
  const { announcements, isLoading, create, toggleActive, remove } =
    useAnnouncements();
  const [form, setForm] = useState<NewAnnouncement>(EMPTY);
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error('Enter a message');
      return;
    }
    setSaving(true);
    try {
      await create({
        ...form,
        message: form.message.trim(),
        ctaLabel: form.ctaLabel?.trim() || undefined,
        ctaUrl: form.ctaUrl?.trim() || undefined,
      });
      setForm(EMPTY);
      toast.success('Announcement created');
    } catch {
      toast.error('Failed to create announcement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-sm text-gray-500 mt-1">
            Active announcements show as a banner at the top of the site.
          </p>
        </div>

        {/* Create form */}
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <input
              type="text"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="e.g. Summer sale - 10% off stays in Batroun this week"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm({ ...form, type: e.target.value as AnnouncementType })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="info">Info</option>
                <option value="promo">Promo</option>
                <option value="warning">Warning</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button label (optional)
              </label>
              <input
                type="text"
                value={form.ctaLabel}
                onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })}
                placeholder="Browse deals"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button link (optional)
              </label>
              <input
                type="text"
                value={form.ctaUrl}
                onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
                placeholder="/search?area=batroun"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            Show immediately
          </label>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create announcement'}
          </button>
        </form>

        {/* List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : announcements.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No announcements yet</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {announcements.map((a) => (
                <li key={a.id} className="p-4 flex items-center gap-4">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TYPE_STYLES[a.type]}`}
                  >
                    {a.type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{a.message}</p>
                    {a.ctaLabel && (
                      <p className="text-xs text-gray-400 truncate">
                        Button: {a.ctaLabel} → {a.ctaUrl}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => toggleActive(a.id, !a.active)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                      a.active
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {a.active ? 'Active' : 'Hidden'}
                  </button>
                  <button
                    onClick={() => remove(a.id)}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnnouncementsPage;
